"use client";

import React from "react";
import dynamic from "next/dynamic";
import { Navigation } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import { useAction } from "next-safe-action/hooks";

import HomeSection from "@/components/home-section";
import TopperCard from "@/components/card/topper";
import SwiperNav from "@/components/swiper-nav";
import { Dialog, DialogContent } from "@/components/ui/dialog";

import { fetchTestimonials } from "@/fetchers/homepage";

import useMoengageEventTracker from "@/hooks/use-moengage";

import EVENT_NAME from "@/hooks/use-moengage/event-name.json";
import EVENT_ATTRIBUTE from "@/hooks/use-moengage/event-attribute.json";

const ShakaPlayer = dynamic(() => import("@/components/shaka-player"), {
  ssr: false,
});

function OurToppers() {
  const trackMoengageEvent = useMoengageEventTracker();

  const prevRef = React.useRef();
  const nextRef = React.useRef();

  const [swiper, setSwiper] = React.useState(null);
  const [video, setVideo] = React.useState(null);

  const { result: { data: toppers = [] } = {}, execute: fetchToppers } =
    useAction(fetchTestimonials);

  React.useEffect(() => {
    fetchToppers();
  }, [fetchToppers]);

  React.useEffect(() => {
    if (swiper) {
      swiper.params.navigation.prevEl = prevRef.current;
      swiper.params.navigation.nextEl = nextRef.current;
      swiper.navigation.init();
      swiper.navigation.update();

      swiper.on("slideNextTransitionEnd", () => {
        trackMoengageEvent(EVENT_NAME.SWIPER_ARROW_CLICKED, {
          [EVENT_ATTRIBUTE.ARROW_TYPE]: "right",
          [EVENT_ATTRIBUTE.VISIBILITY]: "testimonials_ranker",
        });
      });

      swiper.on("slidePrevTransitionEnd", () => {
        trackMoengageEvent(EVENT_NAME.SWIPER_ARROW_CLICKED, {
          [EVENT_ATTRIBUTE.ARROW_TYPE]: "left",
          [EVENT_ATTRIBUTE.VISIBILITY]: "testimonials_ranker",
        });
      });
    }
  }, [swiper]);

  return (
    <HomeSection
      title="Our Rankers"
      subtitle={
        <>
          <span>Success stories from</span>
          <span className="text-primary">StudyIQ Toppers</span>
        </>
      }
      className="block space-y-4"
    >
      <Swiper
        modules={[Navigation]}
        slidesPerView={1.5}
        spaceBetween={16}
        onSwiper={setSwiper}
        breakpoints={{
          480: {
            slidesPerView: 1.5,
          },
          767: {
            slidesPerView: 3,
            slidesPerGroup: 3,
          },
        }}
      >
        {toppers?.map((topper) => (
          <SwiperSlide key={topper.id}>
            <TopperCard {...topper} onVideoSelect={setVideo} />
          </SwiperSlide>
        ))}
      </Swiper>

      <div className="hidden md:flex justify-end">
        <SwiperNav swiper={swiper} prevNavRef={prevRef} nextNavRef={nextRef} />
      </div>

      <Dialog
        open={!!video}
        onOpenChange={(open) => open === false && setVideo(null)}
      >
        <DialogContent className="p-0 overflow-hidden rounded-md sm:max-w-199.25">
          <ShakaPlayer key={video} video={{ videoUrl: video }} autoPlay />
        </DialogContent>
      </Dialog>
    </HomeSection>
  );
}

export default OurToppers;
